import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {fetchBootDeltaAsync} from '../../../srcCommon/services/boot/fetch/bootFetchHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {inAnimationFrameOrHiddenCallbackAsync} from '../../helpers/async/inAnimationFrame';
import {CancelToken} from '../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../helpers/asyncHelpers';
import {ActionWithPayload} from '../../helpers/reduxHelpers';
import {HttpResponseError} from '../../services/http/httpError';
import {ThunkAction} from '../appThunk';
import {getHasServiceWorkerBootCachePatching} from '../featureFlags/featureFlagsSelectors';
import {AccountAction} from './accounts/accountsActions';
import {ActivitiesAction} from './activities/activitiesActions';
import {AnswerFolderAction} from './answerFolders/answerFolderActions';
import {AnswerAction} from './answers/answerActions';
import {ApiTokenAction} from './apiTokens/apiTokensActions';
import {ApplicationsAction} from './applications/applicationsActions';
import {LegacyApplicationsAction} from './applications/legacyApplicationsActions';
import {CalendarEventVersionsAction} from './calendarEventVersions/calendarEventVersionsActions';
import {CalendarVersionsAction} from './calendarVersions/calendarVersionsActions';
import {CardGroupAction} from './cardGroups/cardGroupsActions';
import {CardNoteAction} from './cardNotes/cardNotesActions';
import {CardAction} from './cards/cardsActions';
import {ChannelAction} from './channels/channelsActions';
import {ChatUserOnlineAction} from './chat/chatUserOnlineActions';
import {ChatPrecogAction} from './chatPrecogs/chatPrecogsActions';
import {CommentsAction} from './comments/commentsActions';
import {CompanyAction} from './company/companyActions';
import {ConversationsAction} from './conversations/conversationsActions';
import {CountersAction} from './counters/countersActions';
import {CrmSyncAction} from './crmSync/crmSyncActions';
import {CustomEmojiAction} from './customEmojis/customEmojiActions';
import {CustomFieldsAction} from './customFields/customFieldsActions';
import {CustomViewsAction} from './customViews/customViewsActions';
import {DataGroupsAction} from './dataGroupsActions';
import {DataStateForBoot} from './dataState';
import {DelegationsAction} from './delegations/delegationsActions';
import {ExternalChannelTypesAction} from './externalChannelTypes/externalChannelTypesActions';
import {InboxesAction} from './inboxes/inboxesActions';
import {IntegrationErrorsAction} from './integrations/integrationErrorsActions';
import {IntegrationsAction} from './integrations/integrationsActions';
import {IntegrationTypesAction} from './integrationTypes/integrationTypesActions';
import {JobsAction} from './jobs/jobsActions';
import {LocalApplicationAction} from './localApplications/localApplicationsActions';
import {LocalPreferencesAction} from './localPreferences/localPreferencesActions';
import {MessagesAction} from './messages/messagesActions';
import {OfflineAction} from './offline/offlineActions';
import {PluginsAction} from './plugins/pluginsActions';
import {ConversationPrecogAction} from './precogs/conversationPrecogsActions';
import {PreferencesAction} from './preferences/preferencesActions';
import {RolesAction} from './roles/rolesActions';
import {SchedulingLinkAction} from './schedulingLinks/schedulingLinksActions';
import {SignaturesAction} from './signatures/signaturesActions';
import {TagsAction} from './tags/tagsActions';
import {OnlineTeammatesAction} from './teammates/onlineTeammatesActions';
import {TeamPreferencesAction} from './teamPreferences/teamPreferencesActions';
import {TeamsAction} from './teams/teamsActions';
import {UserMetadataAction} from './userMetadata/userMetadataActions';
import {VacationRespondersAction} from './vacationResponders/vacationRespondersActions';
import {WorkspaceAction} from './workspace/workspaceUpdatedAction';

/*
 * Action types.
 */

export enum DataActionsEnum {
  BOOT = '@@data/BOOT',
}

/*
 * Types.
 */

interface BootActionPayload {
  data: DataStateForBoot;
  deltaSince: number;
}
export interface BootAction extends ActionWithPayload<BootActionPayload> {
  type: DataActionsEnum.BOOT;
}

/*
 * Action creators.
 */

function bootAction(data: DataStateForBoot, deltaSince: number): BootAction {
  return {
    type: DataActionsEnum.BOOT,
    payload: {
      data,
      deltaSince,
    },
  };
}

interface RefreshBootOptions {
  onRawBoot?: HandlerOf<unknown>;
  cancelToken?: CancelToken;
  isRetrying?: boolean;
}

export function refreshBootAction(options: RefreshBootOptions): ThunkAction {
  const {onRawBoot, cancelToken, isRetrying} = options;

  return async (dispatch, getState) => {
    try {
      const initialState = getState();
      const deltasSince = initialState.data.bootFetchedAt;
      const hasServiceWorkerBootCachePatching = getHasServiceWorkerBootCachePatching(initialState);
      const {rawBoot, data} = await fetchBootDeltaAsync(
        initialState.data.company.url,
        deltasSince,
        hasServiceWorkerBootCachePatching,
        cancelToken,
      );

      await inAnimationFrameOrHiddenCallbackAsync(
        () => {
          dispatch(bootAction(data, deltasSince));

          if (onRawBoot) {
            onRawBoot(rawBoot);
          }
        },
        {source: TaskSourcesEnum.PUSHER},
      );
    } catch (error) {
      // Retry once.
      if (!isRetrying) {
        await delayAsync(1000);
        dispatch(refreshBootAction({cancelToken, isRetrying: true, onRawBoot}));
        return;
      }

      // If it's an http error, throw like usual, Bugsnag will mark it accordingly.
      if (error instanceof HttpResponseError) {
        throw error;
      }

      // Otherwise, report to bugsnag for more details.
      reportToBugsnag(new ExtendedError('Failed to refresh the boot.', error));
    }
  };
}

/*
 * Union type.
 */

export type DataAction =
  | BootAction
  | PreferencesAction
  | UserMetadataAction
  | LocalPreferencesAction
  | CountersAction
  | CompanyAction
  | TeamsAction
  | TeamPreferencesAction
  | InboxesAction
  | ChannelAction
  | AnswerAction
  | AnswerFolderAction
  | SignaturesAction
  | DelegationsAction
  | RolesAction
  | CustomViewsAction
  | CustomFieldsAction
  | IntegrationsAction
  | IntegrationErrorsAction
  | JobsAction
  | PluginsAction
  | LegacyApplicationsAction
  | OfflineAction
  | TagsAction
  | ActivitiesAction
  | MessagesAction
  | CommentsAction
  | ConversationsAction
  | CalendarEventVersionsAction
  | SchedulingLinkAction
  | OnlineTeammatesAction
  | ConversationPrecogAction
  | ChatPrecogAction
  | ChatUserOnlineAction
  | DataGroupsAction
  | CustomEmojiAction
  | CardNoteAction
  | WorkspaceAction
  | AccountAction
  | CalendarVersionsAction
  | CardGroupAction
  | CardAction
  | CrmSyncAction
  | VacationRespondersAction
  | ExternalChannelTypesAction
  | IntegrationTypesAction
  | ApplicationsAction
  | ApiTokenAction
  | LocalApplicationAction;
